import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../provider/AuthProvider';
import api from '../../api/api';
import { useInstalledApps } from '../../provider/InstalledAppsProvider';
import useTranslation from '../../hooks/useTranslation';
import { preloadRoute, preloadWhenIdle } from '../../utils/routePreload';
import { FaHome, FaTicketAlt, FaChartBar, FaUsers, FaBuilding, FaBook, FaThLarge, FaComments, FaBullhorn, FaCogs, FaSmile, FaEnvelope, FaPoll, FaStar, FaShieldAlt, FaShareAlt } from 'react-icons/fa';
import { LuBrain, LuListTodo, LuMessagesSquare, LuHandshake, LuCalendarClock, LuCalendarDays, LuActivity, LuShieldCheck, LuPhoneCall, LuSparkles } from 'react-icons/lu';

// import './Sidebar.css';
import dropdownIcon from '../assets/icons/dropdown_icon.svg';
import collapseIcon from '../assets/icons/collapse_icon.svg';
import abrusXenBoxIcon from '../assets/icons/abrus digital_icon.png';
import settingIcon from '../assets/icons/setting_icon.svg';

// Recursive Nav Item Component
const NavItem = ({ item, level = 0, expandedMenus, setExpandedMenus, isCollapsed, setIsCollapsed, isRouteActive, toggleMenu, t, location }) => {
  const hasChildren = item.children && item.children.length > 0;
  // Stable identifier for expansion state — labels are translated and change
  // with the language, which would silently reset expanded menus on switch.
  const menuKey = item.path || item.groupKey || item.label;
  const isExpanded = expandedMenus[menuKey];
  const isActive = isRouteActive(item.path);

  // Check if any child is active to auto-expand or highlight parent
  const isChildActive = hasChildren && item.children.some(child =>
    isRouteActive(child.path) || (child.children && child.children.some(grandChild => isRouteActive(grandChild.path)))
  );

  // Auto-expand when navigating to a child route
  useEffect(() => {
    if (isChildActive) {
      setExpandedMenus(prev => ({ ...prev, [menuKey]: true }));
    }
  }, [location.pathname, isChildActive, menuKey, setExpandedMenus]);

  const handleClick = (e) => {
    if (hasChildren) {
      e.preventDefault(); // Always prevent default for parent items to avoid empty hash navigation

      if (isCollapsed) {
        // If sidebar is collapsed, expand it and open the menu
        setIsCollapsed(false);
        localStorage.setItem('sidebarCollapsed', JSON.stringify(false));
        setExpandedMenus(prev => ({ ...prev, [menuKey]: true }));
      } else {
        // Normal toggle behavior
        toggleMenu(menuKey);
      }
    }
  };

  const indent = { paddingLeft: `${level * 15 + 20}px` };

  return (
    <>
      <li className={`mb-0 ${isActive ? 'active' : ''} ${isChildActive ? 'child-active' : ''}`}>
        <Link
          to={item.path || '#'}
          onMouseEnter={() => preloadRoute(item.path)}
          onFocus={() => preloadRoute(item.path)}
          // The selected module: blue and bold on a soft blue fill, icon included,
          // so it reads at a glance even with the sidebar collapsed. The base
          // colour and weight are left off when active so they cannot win over it.
          className={`flex items-center gap-3 px-2 py-1.5 no-underline rounded-lg transition-colors duration-200 h-8 text-[14px] leading-5 relative focus:bg-gray-50 focus:text-accent ${isActive
            ? 'bg-accent-soft text-accent font-bold'
            : `font-medium hover:bg-gray-50 hover:text-accent ${isChildActive ? 'text-accent' : 'text-text-primary'}`} ${isCollapsed ? 'justify-center p-1.5 w-8 h-8 mx-auto' : ''}`}
          aria-current={isActive ? 'page' : undefined}
          onClick={handleClick}
          title={isCollapsed ? item.label : ''}
          style={level > 0 ? indent : {}}
        >
          {item.icon && (
            <span className={`w-[22px] h-[22px] shrink-0 flex items-center justify-center relative ${isCollapsed ? 'w-[22px] h-[22px] m-0' : ''}`}>
              {typeof item.icon === 'function' ? item.icon() : <item.icon className="w-full h-full block" />}
            </span>
          )}
          {!isCollapsed && (
            <div className="flex-1 flex justify-between items-center w-full">
              <span className="flex-1">{item.label}</span>
              {item.isNexus && !isCollapsed && (
                <span style={{ fontSize: 12, background: 'var(--indigo-500)', color: 'white', padding: '1px 5px', borderRadius: 4, fontWeight: 700, letterSpacing: '0.5px', marginLeft: 4 }}>AI</span>
              )}
              {hasChildren && (
                <span className="text-xs ml-auto">
                  {isExpanded ? '▼' : '▶'}
                </span>
              )}
            </div>
          )}
        </Link>
      </li>
      {hasChildren && isExpanded && !isCollapsed && (
        <ul className="list-none p-0 m-0">
          {item.children.map(child => (
            <NavItem
              key={child.path || child.groupKey || child.label}
              item={child}
              level={level + 1}
              expandedMenus={expandedMenus}
              setExpandedMenus={setExpandedMenus}
              isCollapsed={isCollapsed}
              setIsCollapsed={setIsCollapsed}
              isRouteActive={isRouteActive}
              toggleMenu={toggleMenu}
              t={t}
              location={location}
            />
          ))}
        </ul>
      )}
    </>
  );
};

const NavSection = ({ items, title, expandedMenus, setExpandedMenus, isCollapsed, setIsCollapsed, isRouteActive, toggleMenu, t, dir, location }) => {
  if (items.length === 0) return null;
  return (
    <div className="mb-6">
      {/* A group label, not a page heading: the page's <h1> must come first in the outline. */}
      {title && !isCollapsed && <p className="text-xs font-semibold text-text-secondary tracking-tight uppercase m-0 mb-1 px-1" style={dir === 'rtl' ? { textAlign: 'right' } : {}}>{title}</p>}
      <nav aria-label={title || t('sidebar.main_navigation')}>
        <ul className="list-none p-0 m-0">
          {items.map((item) => (
            <NavItem
              key={item.path || item.groupKey || item.label}
              item={item}
              expandedMenus={expandedMenus}
              setExpandedMenus={setExpandedMenus}
              isCollapsed={isCollapsed}
              setIsCollapsed={setIsCollapsed}
              isRouteActive={isRouteActive}
              toggleMenu={toggleMenu}
              t={t}
              location={location}
            />
          ))}
        </ul>
      </nav>
    </div>
  );
};

const Sidebar = () => {
  const location = useLocation();
  const { t, dir } = useTranslation();
  const { token, userdetails, setUserDetails } = useAuth();
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  // Warm the chunks people open first, once the current page has settled.
  useEffect(() => preloadWhenIdle(['/inbox', '/tickets', '/contacts']), []);
  const dropdownRef = useRef(null);
  const { isAppInstalled, isAppsLoading, aiEnabled } = useInstalledApps();

  // Group Mapping for menu access
  const GROUP_MAPPING = useMemo(() => ({
    'Helpdesk': ['Helpdesk / Viewer', 'Helpdesk / User', 'Helpdesk / Support Agent', 'Helpdesk / Manager'],
    'Contacts': ['Contacts / Viewer', 'Contacts / User', 'Contacts / Manager', 'CRM / Viewer', 'CRM / User', 'CRM / Manager'],
    'CRM': ['CRM / Viewer', 'CRM / User', 'CRM / Manager'],
    'Knowledge System': ['Knowledge System / Viewer', 'Knowledge System / Author', 'Knowledge System / Manager'],
    'Company': ['Administration / Settings'],
    'Users': ['Administration / Access Rights', 'Administration / Settings'],
    'Inbox': ['Inbox / Viewer', 'Inbox / Agent', 'Inbox / Manager'],
    'Marketing': ['Marketing / Viewer', 'Marketing / User', 'Marketing / Manager'],
    'CSAT': ['CSAT / Viewer', 'CSAT / User', 'CSAT / Manager'],
    'Settings': ['Administration / Settings', 'Administration / Access Rights'],
    'Email Services': ['Email / Manager', 'Administration / Settings'],
    'App Store': ['Administration / Settings'],
    'Evox AI': ['Administration / Settings', 'Administration / Access Rights', 'AI & Tech / AI Configurator', 'Nexus / Viewer', 'Nexus / User', 'Nexus / Manager'],
    'Huddle': ['Administration / Settings', 'Huddle / Viewer', 'Huddle / User', 'Huddle / Manager'],
    'Projects': ['Administration / Settings', 'Administration / Access Rights', 'Project / Viewer', 'Project / User', 'Project / Manager'],
    'Admin Monitoring': ['Administration / Settings', 'Administration / Monitoring Viewer', 'Administration / Monitoring Manager'],
    'Social Media': ['Social Media / Viewer', 'Social Media / Agent', 'Social Media / Manager', 'Administration / Settings'],
    'Sentiment Intel': ['Sentiment Intel / Viewer', 'Sentiment Intel / Manager', 'Administration / Settings'],
    'Compliance': ['Compliance / Viewer', 'Compliance / Contributor', 'Compliance / Reviewer', 'Compliance / Approver', 'Compliance / Admin', 'Compliance / Auditor', 'Administration / Settings', 'Helpdesk / User', 'Helpdesk / Support Agent', 'Helpdesk / Manager'],
  }), []);

  const checkGroupAccess = useCallback((label) => {
    if (userdetails?.is_superuser) return true;
    if (!GROUP_MAPPING[label]) return true;
    const allowedGroups = GROUP_MAPPING[label];
    const userGroups = userdetails?.group_names || [];
    return userGroups.some(group => allowedGroups.includes(group));
  }, [userdetails, GROUP_MAPPING]);





  useEffect(() => {
    const checkPermissions = async () => {
      if (token && userdetails && !userdetails.group_names && !userdetails.is_superuser) {
        try {
          const response = await api.get('/auth/users/me/');
          if (response.data && response.data.group_names) {
            const updatedUser = { ...userdetails, ...response.data };
            setUserDetails(updatedUser);
            localStorage.setItem('userdetails', JSON.stringify(updatedUser));
          }
        } catch (error) {
          console.error('Error refreshing user permissions:', error);
        }
      }
    };
    checkPermissions();
  }, [token, userdetails, setUserDetails]);

  const [isCollapsed, setIsCollapsed] = useState(() => {
    try { return JSON.parse(localStorage.getItem('sidebarCollapsed')) || false; }
    catch { return false; }
  });

  const [expandedMenus, setExpandedMenus] = useState({});

  useEffect(() => {
    const appContainer = document.querySelector('.app-container');
    if (appContainer) {
      if (isCollapsed) {
        appContainer.classList.add('sidebar-collapsed');
      } else {
        appContainer.classList.remove('sidebar-collapsed');
      }
    }
  }, [isCollapsed]);

  const toggleMenu = (label) => {
    setExpandedMenus(prev => ({
      ...prev,
      [label]: !prev[label]
    }));
  };

  // Trial-expired modules stay visible while installed — clicking them lands
  // on /trial-expired via ProtectedRoute. Only uninstalling removes the entry.
  const checkAppInstalled = useCallback((item) => {
    if (!item.appId) return true;
    if (isAppsLoading) return true;
    return isAppInstalled(item.appId);
  }, [isAppInstalled, isAppsLoading]);

  const navItems = [
    // The order the menu shows (agreed 2026-10-26): Home, Huddle, Voice, Inbox, Contacts, Helpdesk, Knowledge,
    // Sentiment, CSAT, Evox, Dashboard. CRM sits by Contacts and Compliance by Helpdesk, which they build on;
    // the other apps follow Dashboard. Monitoring is in the Services section below.
    { path: '/home', icon: LuSparkles, label: t('nav.home'), groupKey: 'Dashboard', appId: 'nexus', requiresAi: true },
    { path: '/huddle', icon: LuMessagesSquare, label: t('nav.huddle'), groupKey: 'Huddle', appId: 'huddle' },
    { path: '/voice', icon: LuPhoneCall, label: t('nav.voice'), groupKey: 'Voice', appId: 'voice' },
    { path: '/inbox', icon: FaComments, label: t('nav.inbox'), groupKey: 'Inbox', appId: 'inbox' },
    { path: '/contacts', icon: FaUsers, label: t('nav.contacts'), groupKey: 'Contacts', appId: 'partner' },
    {
      label: t('nav.crm'),
      icon: FaChartBar,
      groupKey: 'CRM',
      appId: 'crm',
      path: '/crm',
    },
    { path: '/tickets', icon: FaTicketAlt, label: t('nav.helpdesk'), groupKey: 'Helpdesk', appId: 'helpdesk' },
    { path: '/compliance', icon: LuShieldCheck, label: t('nav.compliance'), groupKey: 'Compliance', appId: 'regulatory' },
    { path: '/knowledge-system', icon: FaBook, label: t('nav.knowledge_system'), groupKey: 'Knowledge System', appId: 'knowledge_system' },
    { path: '/sentiment/dashboard', icon: LuActivity, label: t('nav.sentiment_intel'), groupKey: 'Sentiment Intel', appId: 'sentiment_analysis', requiresAi: true },
    { path: '/csat', icon: FaSmile, label: t('nav.csat'), groupKey: 'CSAT', appId: 'customer_satisfaction' },
    { path: '/nexus', icon: LuBrain, label: t('page.nexus_ai'), groupKey: 'Evox AI', appId: 'nexus', isNexus: true, requiresAi: true },
    { path: '/dashboard', icon: FaHome, label: t('nav.dashboard'), groupKey: 'Dashboard', matchRoot: true },
    { path: '/projects', icon: LuListTodo, label: t('nav.projects'), groupKey: 'Projects', appId: 'projects' },
    { path: '/calendar', icon: LuCalendarDays, label: t('nav.calendar'), groupKey: 'Calendar', appId: 'calendar' },
    { path: '/appointments', icon: LuCalendarClock, label: t('nav.appointments'), groupKey: 'Appointments', appId: 'appointments' },
    { path: '/marketing', icon: FaBullhorn, label: t('nav.marketing'), groupKey: 'Marketing', appId: 'marketing' },
    { path: '/loyalty', icon: FaStar, label: t('nav.loyalty'), groupKey: 'Marketing', appId: 'loyalty' },
    { path: '/social-media', icon: FaShareAlt, label: t('nav.social_media'), groupKey: 'Social Media', appId: 'social_media' },
  ].filter(item =>
    checkGroupAccess(item.groupKey || item.label) &&
    checkAppInstalled(item)
    // AI-only modules stay in the menu while installed even when the tenant's
    // AI flag is off: hiding them made an installed module look missing. Their
    // pages explain that AI is disabled and how to turn it on.
  );

  const serviceItems = [
    {
      label: t('nav.settings'),
      groupKey: 'Settings',
      icon: () => <img src={settingIcon} alt={t('nav.settings')} style={{ width: '22px', height: '22px' }} />,
      path: '/settings',
      children: [
        { path: '/settings', icon: FaThLarge, label: t('nav.overview') },
        { path: '/users/list', icon: FaUsers, label: t('nav.users'), groupKey: 'Users' },
        { path: '/integrations', icon: FaPoll, label: t('nav.integrations'), groupKey: 'Integrations' },
        {
          label: t('nav.configuration'),
          icon: FaCogs,
          groupKey: 'Settings',
          children: [
            { path: '/settings/ai', label: t('nav.ai_engine'), appId: 'ai_engine' },
            { path: '/settings/translations', label: t('nav.translations') },
            { path: '/settings/resource-calendars', label: t('nav.resource_calendars'), appId: 'company_mgmt' },
            // No appId: API keys are platform infrastructure, not an app-store
            // module, so there is no AppRegistry entry to check. Leaving one here
            // makes isAppInstalled('api_keys') return false and hides the item
            // from everyone. Visibility is governed by the parent Configuration
            // item's groupKey ('Settings' -> Administration / Settings |
            // Access Rights), which matches the IsApiKeyAdmin gate on the API.
            { path: '/settings/api-keys', label: t('nav.api_keys') },
            // The look of documents the assistant writes; needs the assistant (Nexus).
            { path: '/settings/document-branding', label: t('nav.document_branding'), appId: 'nexus' },
          ].filter(item => checkAppInstalled(item))
        }
      ].filter(item => {
        if (item.hideForNonSuperuser && !userdetails?.is_superuser) return false;
        return checkGroupAccess(item.groupKey || item.label) && checkAppInstalled(item);
      })
    },
    {
      label: t('nav.email_services'),
      icon: FaEnvelope,
      groupKey: 'Email Services',
      children: [
        { path: '/settings/emails', label: t('nav.configuration') },
        { path: '/email/templates', label: t('nav.templates') },
        { path: '/email-logs', label: t('nav.logs') }
      ]
    },
    { path: '/apps', icon: FaThLarge, label: t('nav.app_store'), groupKey: 'App Store' },
    { path: '/admin/monitoring', icon: FaShieldAlt, label: t('nav.monitoring'), groupKey: 'Admin Monitoring', appId: 'admin_monitoring' },
    ...(userdetails?.is_superuser ? [{ path: '/saas-admin', icon: () => (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7"></rect><rect x="14" y="3" width="7" height="7"></rect><rect x="14" y="14" width="7" height="7"></rect><rect x="3" y="14" width="7" height="7"></rect></svg>), label: t('nav.saas_admin'), groupKey: 'SaaSAdmin' }] : []),
    // Backup & Restore shares the SaaSAdmin group key so it is governed by the
    // same superuser gate as the SaaS admin console.
    ...(userdetails?.is_superuser ? [{ path: '/saas-admin/backups', icon: () => (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><ellipse cx="12" cy="5" rx="9" ry="3"></ellipse><path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3"></path><path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"></path></svg>), label: t('nav.backup_restore'), groupKey: 'SaaSAdmin' }] : []),
    ...(userdetails?.is_superuser ? [{ path: '/saas-admin/voice-pricing', icon: () => (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9"></circle><path d="M14.5 9.5a2.5 2.5 0 0 0-5 0c0 2.5 5 2.5 5 5a2.5 2.5 0 0 1-5 0"></path><path d="M12 6v1.5M12 16.5V18"></path></svg>), label: t('nav.voice_pricing'), groupKey: 'SaaSAdmin' }] : [])
  ].filter(item => {
    if (item.hideForNonSuperuser && !userdetails?.is_superuser) return false;
    // Monitoring is an app-store module; the other items here have no appId and always pass.
    return checkGroupAccess(item.groupKey || item.label) && checkAppInstalled(item);
  });

  const handleCollapseToggle = () => {
    const newCollapsedState = !isCollapsed;
    setIsCollapsed(newCollapsedState);
    localStorage.setItem('sidebarCollapsed', JSON.stringify(newCollapsedState));
  };

  const isRouteActive = (path, matchRoot = false) => {
    if (!path) return false;
    if (matchRoot && path === '/dashboard') {
      return location.pathname === '/' || location.pathname === '/dashboard';
    }
    return location.pathname.startsWith(path);
  };

  useEffect(() => {
    const handleToggleSidebar = () => setIsMobileOpen(prev => !prev);
    window.addEventListener('toggle-sidebar', handleToggleSidebar);
    return () => window.removeEventListener('toggle-sidebar', handleToggleSidebar);
  }, []);

  return (
    <>
      {isMobileOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[1099] md:hidden" onClick={() => setIsMobileOpen(false)} />
      )}
      <aside className={`cx-sidebar ${isCollapsed ? 'collapsed' : ''} w-[220px] h-screen bg-white fixed top-0 z-[1100] flex flex-col transition-all duration-300 font-manrope overflow-hidden ${dir === 'rtl' ? 'right-0 border-l border-[color:light-dark(#f7f8fa,#2a2e36)]' : 'left-0 border-r border-[color:light-dark(#f7f8fa,#2a2e36)]'} ${isCollapsed ? 'w-[60px]' : ''} ${isMobileOpen ? 'translate-x-0' : 'max-md:-translate-x-full'}`}>
        <div className={`p-4 shrink-0 flex items-center gap-3 border-b border-[color:light-dark(#f7f8fa,#2a2e36)] mb-4 ${isCollapsed ? 'justify-center px-2' : ''}`}>
          <div className={`w-8 h-8 rounded-lg  flex items-center justify-center shrink-0 shadow-sm`}><img src={abrusXenBoxIcon} alt="XENBOX" className="w-6 h-6" /></div>
          {!isCollapsed && <span className="text-lg font-bold text-ink tracking-tight">XenBox</span>}
        </div>
        <div className={`flex-1 overflow-y-auto px-2 ${isCollapsed ? 'overflow-hidden' : ''}`}>
          <NavSection items={navItems} expandedMenus={expandedMenus} setExpandedMenus={setExpandedMenus} isCollapsed={isCollapsed} setIsCollapsed={setIsCollapsed} isRouteActive={isRouteActive} toggleMenu={toggleMenu} t={t} dir={dir} location={location} />
          <NavSection items={serviceItems} title={t('sidebar.services')} expandedMenus={expandedMenus} setExpandedMenus={setExpandedMenus} isCollapsed={isCollapsed} setIsCollapsed={setIsCollapsed} isRouteActive={isRouteActive} toggleMenu={toggleMenu} t={t} dir={dir} location={location} />
        </div>
        <div className={`shrink-0 p-4 flex flex-col items-start justify-start gap-4 relative border-t border-line-6 bg-white ${isCollapsed ? 'px-2 gap-3 flex-col items-center justify-center' : ''}`}>
          <div className="flex items-center justify-center w-full">
            <button className="shrink-0 w-8 h-8 bg-bg-gray border-none rounded-full flex items-center justify-center cursor-pointer transition-colors duration-200 text-gray-66 hover:bg-hover-gray focus:bg-hover-gray" onClick={handleCollapseToggle} title={isCollapsed ? t('sidebar.expand_sidebar') : t('sidebar.collapse_sidebar')}>
              <img src={collapseIcon} alt={isCollapsed ? "Expand" : "Collapse"} className={`w-4 h-4 transition-transform duration-300 ${isCollapsed ? 'rotate-180' : ''}`} />
            </button>
          </div>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;